/* @flow */

import { route } from 'preact-router';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import VanillaToasts from 'vanillatoasts';
import { createSelector } from 'reselect';
import { selecionarEnderecoPorId } from './endereco';
import xhr from '../../utils/xhr';
import pesquisarCepEnderecoEntrega from '../../enderecos/pesquisar-cep-endereco-entrega';
import pesquisarLogradouroEnderecoEntrega from '../../enderecos/pesquisar-logradouro-endereco-entrega';
import { type EnderecoPesquisa } from '../../enderecos/types';
import { type StateCadastroEndereco } from './types';
import { type GetState, type State } from '../../preact-app/types';
import * as Loading from '../loading';
import { type EtapaLoading } from '../loading-types';
import * as empresa from '../empresa';
import sleep from '../../utils/sleep';
import { getClientesId } from './pedido';
import jsonEstados from '../../utils/json-estados.json';
import { type Estado } from '../../utils/get-json-estados';

type SetEnderecoPesquisaAction = {
    type: 'balcao/cadastro-endereco/SET_ENDERECO_PESQUISA',
} & EnderecoPesquisa;

type SetEntregaDisponivelAction = {
    type: 'balcao/cadastro-endereco/SET_ENTREGA_DISPONIVEL',
    entregaDisponivel: boolean,
};

type SetEnderecosDisponiveisAction = {
    type: 'balcao/cadastro-endereco/SET_ENDERECOS_DISPONIVEIS',
    enderecosDisponiveis: EnderecoPesquisa[],
};

type SetPorNomeAction = {
    type: 'balcao/cadastro-endereco/SET_POR_NOME',
    nome: string,
    valor: string,
};

type SetCepAction = {
    type: 'balcao/cadastro-endereco/SET_CEP',
    cep: string,
};

type SetEstadoAction = {
    type: 'balcao/cadastro-endereco/SET_ESTADO',
    estado: string,
};

type LimparAction = {
    type: 'balcao/cadastro-endereco/LIMPAR',
};

type ToggleCadastrarViaLogradouroAction = {
    type: 'balcao/cadastro-endereco/TOGGLE_CADASTRAR_VIA_LOGRADOURO',
};

type SelecionarEnderecoDisponivel = {
    type: 'balcao/cadastro-endereco/SELECIONAR_ENDERECO_DISPONIVEL',
    endereco: EnderecoPesquisa,
};

type Action =
    | SetEntregaDisponivelAction
    | SetPorNomeAction
    | SetCepAction
    | SetEstadoAction
    | LimparAction
    | ToggleCadastrarViaLogradouroAction
    | SetEnderecosDisponiveisAction
    | SelecionarEnderecoDisponivel
    | SetEnderecoPesquisaAction;

// Action Types

const SET_ENDERECO_PESQUISA = 'balcao/cadastro-endereco/SET_ENDERECO_PESQUISA';
const SET_CEP = 'balcao/cadastro-endereco/SET_CEP';
const SET_ESTADO = 'balcao/cadastro-endereco/SET_ESTADO';
const SET_POR_NOME = 'balcao/cadastro-endereco/SET_POR_NOME';
const SET_ENTREGA_DISPONIVEL = 'balcao/cadastro-endereco/SET_ENTREGA_DISPONIVEL';
const LOADING_VALIDANDO_ENTREGA = 'LOADING_VALIDANDO_ENTREGA';
const LIMPAR = 'balcao/cadastro-endereco/LIMPAR';
const TOGGLE_CADASTRAR_VIA_LOGRADOURO = 'balcao/cadastro-endereco/TOGGLE_CADASTRAR_VIA_LOGRADOURO';
const SET_ENDERECOS_DISPONIVEIS = 'balcao/cadastro-endereco/SET_ENDERECOS_DISPONIVEIS';
const SELECIONAR_ENDERECO_DISPONIVEL = 'balcao/cadastro-endereco/SELECIONAR_ENDERECO_DISPONIVEL';

// Action Creators

const setEntregaDisponivel = () => ({
    type: SET_ENTREGA_DISPONIVEL,
    entregaDisponivel: true,
});

const setEntregaIndisponivel = () => ({
    type: SET_ENTREGA_DISPONIVEL,
    entregaDisponivel: false,
});

const setEnderecoPesquisa = (endereco: EnderecoPesquisa): SetEnderecoPesquisaAction => ({
    type: SET_ENDERECO_PESQUISA,
    ...endereco,
});

const setEnderecosDisponiveis = (
    enderecosDisponiveis: EnderecoPesquisa[],
): SetEnderecosDisponiveisAction => ({
    type: SET_ENDERECOS_DISPONIVEIS,
    enderecosDisponiveis,
});

export const setPorNome = (nome: string, valor: string) => ({
    type: SET_POR_NOME,
    nome,
    valor,
});

export const setCep = (cep: string): SetCepAction => ({
    type: SET_CEP,
    cep,
});

export const setEstado = (estado: string): SetEstadoAction => ({
    type: SET_ESTADO,
    estado,
});

export const toggleCadastrarViaLogradouro = (): ToggleCadastrarViaLogradouroAction => ({
    type: TOGGLE_CADASTRAR_VIA_LOGRADOURO,
});


export const selecionarEnderecoDisponivel = (endereco: EnderecoPesquisa) => ({
    type: SELECIONAR_ENDERECO_DISPONIVEL,
    endereco,
});

export const limpar = () => ({ type: LIMPAR });

// Selectors

export const getCadastroEndereco = (state: State) => state.balcao.cadastroEndereco;
const getEstado = (state: State) => getCadastroEndereco(state).estado;
const getCep = (state: State) => getCadastroEndereco(state).cep;
const getCadastrarViaLogradouro = (state: State) => getCadastroEndereco(state)
    .cadastrarViaLogradouro;
const isEntregaDisponivel = (state: State) => getCadastroEndereco(state).entregaDisponivel;
const getDadosEndereco = createSelector(
    [getCadastroEndereco, getClientesId],
    (state: StateCadastroEndereco, clientesId) => ({
        usuariosId: clientesId,
        cep: state.cep,
        logradouro: state.logradouro,
        bairro: state.bairro,
        cidade: state.cidade,
        estado: state.estado,
        numero: state.numero,
        complemento: state.complemento,
        pontoReferencia: state.pontoReferencia,
    }),
);

export const getIsValidandoEntrega: State => ?EtapaLoading = createSelector(
    Loading.getLoading,
    loading => (
        loading.find(({ nome }) => nome === LOADING_VALIDANDO_ENTREGA)
    ),
);

export const getEstados: State => Estado[] = createSelector(
    [getCadastrarViaLogradouro, getEstado],
    (cadastrarViaLogradouro, estado) => (
        !cadastrarViaLogradouro
            ? jsonEstados.estados.filter(({ sigla }) => sigla === estado)
            : jsonEstados.estados
    ),
);

export const getCidadesDisponiveis: State => string[] = createSelector(
    [getCadastrarViaLogradouro, getEstado],
    (cadastrarViaLogradouro, estado) => {
        if (!cadastrarViaLogradouro) {
            return [];
        }
        return jsonEstados
            .estados
            .filter(({ sigla }) => estado === sigla)
            .flatMap(({ cidades }) => cidades);
    },
);

// Thunks

export const enviarCadastroEndereco = () => (dispatch: Function, getState: GetState) => {
    if (!isEntregaDisponivel(getState())) {
        VanillaToasts.create({
            type: 'danger',
            title: 'Serviço não é atendido nesta região.',
            timeout: 5000,
        });
        return Promise.resolve();
    }

    return xhr.post('/enderecos', getDadosEndereco(getState()))
        .then(({ data: { id } }) => {
            dispatch(selecionarEnderecoPorId(id));
            route('/balcao/produtos');
        })
        .catch((error) => {
            VanillaToasts.create({
                type: 'danger',
                title: get(error, 'response.data.message', 'Houve um erro ao cadastrar.'),
                timeout: 5000,
            });
        });
};

export const carregarEstadoEmpresa = () => (dispatch: Function, getState: GetState) => (
    dispatch(empresa.buscarEndereco())
        .then(() => dispatch(setEstado(empresa.getEstado(getState()))))
);

export const setCepThunk = (() => {
    const validarViaCep = debounce((dispatch: Function, getState: GetState) => {
        if (getCadastrarViaLogradouro(getState())) {
            return;
        }

        const cep = getCep(getState());
        if (cep.length !== 8) {
            return;
        }

        dispatch(Loading.init([{ nome: LOADING_VALIDANDO_ENTREGA, mensagem: 'Validando local de entrega' }]));
        dispatch(Loading.loading(LOADING_VALIDANDO_ENTREGA));
        pesquisarCepEnderecoEntrega(cep).then(({ data }) => {
            dispatch(Loading.success(LOADING_VALIDANDO_ENTREGA, 'Local de entrega válido.'));
            sleep(5000).then(() => dispatch(Loading.destroy()));
            dispatch(setEntregaDisponivel());
            dispatch(setEnderecosDisponiveis([data]));
            dispatch(setEnderecoPesquisa(data));
        }).catch((err) => {
            const mensagem = get(err, 'response.data.message', 'Cep não encontrado.');
            dispatch(Loading.error(LOADING_VALIDANDO_ENTREGA, mensagem));
            dispatch(setEntregaIndisponivel());
            sleep(5000).then(() => dispatch(Loading.destroy()));
        });
    }, 500);

    return (cep: string) => (dispatch: Function, getState: GetState) => {
        dispatch(setCep(cep.replace(/\D/g, '')));
        validarViaCep(dispatch, getState);
    };
})();

export const buscarEnderecosDisponiveis = (logradouro: string) => (
    dispatch: Function,
    getState: GetState,
): Promise<void> => {
    if (!getCadastrarViaLogradouro(getState())) {
        return Promise.resolve();
    }

    const { estado, cidade } = getCadastroEndereco(getState());
    if (!logradouro) {
        dispatch(setEnderecosDisponiveis([]));
        dispatch(Loading.destroy());
        return Promise.resolve();
    }

    dispatch(Loading.init([{ nome: LOADING_VALIDANDO_ENTREGA, mensagem: 'Procurando endereços disponíveis.' }]));
    dispatch(Loading.loading(LOADING_VALIDANDO_ENTREGA));
    return pesquisarLogradouroEnderecoEntrega({ estado, cidade, logradouro })
        .then(({ data }) => {
            if (data.length === 0) {
                throw new Error();
            }

            dispatch(Loading.success(LOADING_VALIDANDO_ENTREGA, 'Endereços disponíveis encontrados'));
            dispatch(setEnderecosDisponiveis(data));
            // sleep(5000).then(() => dispatch(Loading.destroy()));
        })
        .catch((err) => {
            const mensagem = get(err, 'response.data.message', 'Nenhum endereço correspondente disponível.');
            dispatch(setEnderecosDisponiveis([]));
            dispatch(Loading.error(LOADING_VALIDANDO_ENTREGA, mensagem));
            // sleep(5000).then(() => dispatch(Loading.destroy()));
        });
};

const initialState = {
    cep: '',
    logradouro: '',
    bairro: '',
    cidade: '',
    estado: '',
    numero: '',
    complemento: '',
    pontoReferencia: '',
    entregaDisponivel: false,
    cadastrarViaLogradouro: false,
    enderecosDisponiveis: [],
};

const cadastroEnderecoReducer = (
    state: StateCadastroEndereco = initialState,
    action: Action,
): StateCadastroEndereco => {
    switch (action.type) {
    case SET_ENDERECO_PESQUISA:
        return {
            ...state,
            cep: action.cep,
            logradouro: action.logradouro,
            bairro: action.bairro,
            cidade: action.cidade,
            estado: action.estado,
        };
    case SET_CEP:
        return {
            ...state,
            cep: action.cep,
            entregaDisponivel: false,
        };
    case SET_ESTADO:
        return {
            ...state,
            estado: action.estado,
            cidade: '',
        };
    case SET_POR_NOME:
        return {
            ...state,
            [action.nome]: action.valor,
        };
    case SET_ENTREGA_DISPONIVEL:
        return {
            ...state,
            entregaDisponivel: action.entregaDisponivel,
        };
    case SET_ENDERECOS_DISPONIVEIS:
        return {
            ...state,
            enderecosDisponiveis: action.enderecosDisponiveis,
        };
    case SELECIONAR_ENDERECO_DISPONIVEL:
        return {
            ...state,
            cep: action.endereco.cep,
            logradouro: action.endereco.logradouro,
            bairro: action.endereco.bairro,
            cidade: action.endereco.cidade,
            estado: action.endereco.estado,
            entregaDisponivel: true,
        };
    case TOGGLE_CADASTRAR_VIA_LOGRADOURO:
        return {
            ...state,
            cadastrarViaLogradouro: !state.cadastrarViaLogradouro,
            entregaDisponivel: false,
            cep: '',
            logradouro: '',
            bairro: '',
            cidade: '',
            enderecosDisponiveis: [],
        };
    case LIMPAR:
        return initialState;
    default:
        return state;
    }
};

export default cadastroEnderecoReducer;
